import React from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';
import type {
  ConnectorParameterBindingKind,
  ConnectorParameterDestination,
  ConnectorParameterTarget,
} from '../../types';
import {
  borderRadius,
  getControlBorderColor,
  layoutGap,
} from '../../../theme/theme';
import { BodyText, CaptionText } from '../typography';
import { WorkflowNotice } from './WorkflowStepComponents';

interface ParameterModeOption extends ConnectorParameterTarget {
  label: string;
}

const PROJECT_MODES: ParameterModeOption[] = [
  { destination: 'project', bindingKind: 'type', label: 'Project type' },
  { destination: 'project', bindingKind: 'instance', label: 'Project instance' },
];

const FAMILY_MODES: ParameterModeOption[] = [
  { destination: 'family', bindingKind: 'type', label: 'Family type' },
  { destination: 'family', bindingKind: 'instance', label: 'Family instance' },
];

export function formatParameterSetupTargetLabel(
  destination: ConnectorParameterDestination,
  bindingKind: ConnectorParameterBindingKind
): string {
  return `${destination === 'project' ? 'Project' : 'Family'} ${bindingKind}`;
}

function parseParameterSetupMode(value: string): ConnectorParameterTarget | null {
  const [destination, bindingKind] = value.split(':');
  if (
    (destination === 'project' || destination === 'family') &&
    (bindingKind === 'type' || bindingKind === 'instance')
  ) {
    return { destination, bindingKind };
  }

  return null;
}

interface ParameterSetupModeSelectorProps {
  target: ConnectorParameterTarget;
  onTargetChange: (target: ConnectorParameterTarget) => void;
  legend: string;
  ariaLabel: string;
  help?: ReactNode;
  impact: ReactNode;
  impactHelp?: ReactNode;
  /** Render impactHelp as the notice leading icon instead of the info icon. */
  impactHelpAsNoticeIcon?: boolean;
  minimal?: boolean;
  disabled?: boolean;
  /** Mode PipeEX recommends for this setup; gets a "PipeEX choice" star marker. */
  recommendedTarget?: ConnectorParameterTarget;
}

const ParameterSetupModeSelector: React.FC<ParameterSetupModeSelectorProps> = ({
  target,
  onTargetChange,
  legend,
  ariaLabel,
  help,
  impact,
  impactHelp,
  impactHelpAsNoticeIcon = false,
  minimal = false,
  disabled = false,
  recommendedTarget,
}) => {
  const theme = useTheme();
  const selectedValue = `${target.destination}:${target.bindingKind}`;
  const familySelected = target.destination === 'family';
  const selectedTargetLabel = formatParameterSetupTargetLabel(
    target.destination,
    target.bindingKind
  );
  const [familyOptionsExpanded, setFamilyOptionsExpanded] = React.useState(familySelected);
  const familyOptionsId = React.useId();

  React.useEffect(() => {
    if (familySelected) {
      setFamilyOptionsExpanded(true);
    }
  }, [familySelected]);

  const renderMode = (mode: ParameterModeOption) => {
    const modeValue = `${mode.destination}:${mode.bindingKind}`;
    const selected = selectedValue === modeValue;
    const recommended =
      recommendedTarget != null &&
      recommendedTarget.destination === mode.destination &&
      recommendedTarget.bindingKind === mode.bindingKind;

    return (
      <FormControlLabel
        key={modeValue}
        value={modeValue}
        control={
          <Radio size="small" disabled={disabled} inputProps={{ 'aria-label': mode.label }} />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: layoutGap.micro,
              minWidth: 0,
              width: '100%',
            }}
          >
            <CaptionText component="span" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}>
              {mode.label}
            </CaptionText>
            {recommended && (
              <Tooltip title="PipeEX choice" arrow>
                <StarIcon
                  aria-label="PipeEX choice"
                  sx={{
                    fontSize: 12,
                    color: theme.palette.warning.main,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
          </Box>
        }
        sx={{
          m: 0,
          flex: '0 1 176px',
          minWidth: 150,
          minHeight: 30,
          px: layoutGap.dense,
          py: layoutGap.micro,
          alignItems: 'center',
          borderRadius: `${borderRadius.md}px`,
          border: `1px solid ${
            selected ? alpha(theme.palette.primary.main, 0.42) : getControlBorderColor(theme)
          }`,
          backgroundColor: selected
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.07 : 0.12)
            : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.5 : 0.14),
          transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
          '&:hover': {
            borderColor: selected
              ? alpha(theme.palette.primary.main, 0.55)
              : alpha(theme.palette.text.primary, 0.24),
            backgroundColor: selected
              ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.16)
              : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.75 : 0.2),
          },
          '& .MuiRadio-root': { p: layoutGap.dense, mr: layoutGap.micro },
          '& .MuiSvgIcon-root': { fontSize: 16 },
          '& .MuiFormControlLabel-label': { minWidth: 0, width: '100%' },
        }}
      />
    );
  };

  const toggleLabel = `${familyOptionsExpanded ? 'Hide' : 'Show'} advanced family parameter modes${
    familySelected ? `. ${selectedTargetLabel} selected` : ''
  }`;
  const disclosureLabel = familySelected
    ? `${selectedTargetLabel} (advanced)`
    : 'Family parameters (advanced)';

  return (
    <FormControl component="fieldset" size="small" sx={{ minWidth: 0, width: '100%' }}>
      <FormLabel component="legend" sx={{ mb: minimal ? layoutGap.dense : layoutGap.comfort }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense }}>
          <CaptionText sx={{ fontWeight: 700 }}>{legend}</CaptionText>
          {help}
        </Box>
      </FormLabel>

      <RadioGroup
        value={selectedValue}
        onChange={(event) => {
          const nextTarget = parseParameterSetupMode(event.target.value);
          if (nextTarget) {
            onTargetChange(nextTarget);
          }
        }}
        aria-label={ariaLabel}
        sx={{ display: 'grid', gap: layoutGap.dense }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: layoutGap.dense,
          }}
        >
          {PROJECT_MODES.map((mode) => renderMode(mode))}

          <Button
            type="button"
            size="small"
            variant="outlined"
            color={familySelected ? 'primary' : 'inherit'}
            aria-label={toggleLabel}
            aria-expanded={familyOptionsExpanded}
            aria-controls={familyOptionsId}
            disabled={disabled}
            onClick={() => setFamilyOptionsExpanded((expanded) => !expanded)}
            endIcon={
              <ExpandMoreRoundedIcon
                aria-hidden
                sx={{
                  fontSize: 17,
                  transform: familyOptionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create('transform'),
                }}
              />
            }
            sx={{
              minHeight: 30,
              px: layoutGap.comfort,
              borderRadius: `${borderRadius.md}px`,
              borderColor: familySelected
                ? alpha(theme.palette.primary.main, 0.48)
                : getControlBorderColor(theme),
              color: familySelected ? theme.palette.primary.light : theme.palette.text.secondary,
              backgroundColor: familySelected
                ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.07 : 0.12)
                : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.5 : 0.14),
              '&:hover': {
                borderColor: familySelected
                  ? alpha(theme.palette.primary.main, 0.65)
                  : alpha(theme.palette.text.primary, 0.26),
                backgroundColor: familySelected
                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.16)
                  : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.75 : 0.2),
              },
              '& .MuiButton-endIcon': { ml: layoutGap.dense },
            }}
          >
            {/* Match the project-mode radio labels (CaptionText, bold) instead
                of the MUI button font. */}
            <CaptionText component="span" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'inherit' }}>
              {disclosureLabel}
            </CaptionText>
          </Button>
        </Box>

        <Collapse in={familyOptionsExpanded} unmountOnExit>
          <Box
            id={familyOptionsId}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: layoutGap.dense, maxWidth: 760 }}
          >
            {FAMILY_MODES.map((mode) => renderMode(mode))}
          </Box>
        </Collapse>
      </RadioGroup>

      {familySelected && impact ? (
        <WorkflowNotice
          severity="info"
          variant="workspace"
          icon={impactHelpAsNoticeIcon ? impactHelp : undefined}
          sx={{
            mt: layoutGap.dense,
            maxWidth: 760,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: layoutGap.row,
            }}
          >
            <BodyText component="span" sx={{ color: 'inherit', lineHeight: 1.3, minWidth: 0 }}>
              {impact}
            </BodyText>
            {impactHelpAsNoticeIcon ? null : impactHelp}
          </Box>
        </WorkflowNotice>
      ) : null}
    </FormControl>
  );
};

export default ParameterSetupModeSelector;
